import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BarChartIcon from '@mui/icons-material/BarChart';
import EditIcon from '@mui/icons-material/Edit';
import PaymentIcon from '@mui/icons-material/Payment';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TablePagination,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Radio,
	Link,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	InputAdornment,
} from '@mui/material';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminSearchUsers, useAdminSwitchUser } from 'services/admin';
import { useAdminMonitoringData, useGetCropById, useInvoiceDetails } from 'services/monitoring-admin/monitoringAdminHooks';
import { MonitoringAdminData, CropData, InvoiceDetails } from 'services/monitoring-admin/monitoringAdminService';
import { useLogout } from 'services/auth';
import { format } from 'date-fns';
import NewFileUploader from '@components/NewFileUploader';
import { GeneralMuiModal } from '@components/shared/general-mui-modal';
import { TaMaps } from '@components/shared/Maps/TaMaps';
import { useModalDimensions } from '@hooks/useModalDimensions';
import { useNavigate } from 'react-router-dom';
import ClearIcon from '@mui/icons-material/Clear';

// Component to display crop name with loading state
const CropNameCell = ({ cropId, getCropName }: { cropId: string, getCropName: (id: string) => Promise<string> }) => {
  const [name, setName] = useState<string>('Loading...');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const fetchedRef = useRef(false);
  
  useEffect(() => {
    // Only fetch once per component instance to prevent infinite loops
    if (!fetchedRef.current && cropId) {
      fetchedRef.current = true;
      
      const fetchCrop = async () => {
        try {
          const cropName = await getCropName(cropId);
          setName(cropName);
        } catch (error) {
          console.error('Error fetching crop name:', error);
          setName('Error');
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchCrop();
    }
  }, [cropId]); // Intentionally omitting getCropName from deps to prevent re-fetching
  
  return isLoading ? (
    <CircularProgress size={16} />
  ) : (
    <span>{name}</span>
  );
};

export const AdminPage = () => {
	const { t } = useTranslation();
	const { logout } = useLogout();
	const {
		searchUsers,
		users,
		isLoading: searchLoading,
		error: searchError,
		setUsers, // Add setUsers function from the hook
	} = useAdminSearchUsers();
	const { switchToUser, isLoading: switchLoading } = useAdminSwitchUser();
	const modalDimensions = useModalDimensions();
	const navigate = useNavigate();
	
	// Monitoring data state
	const { 
		data: monitoringData, 
		isLoading: monitoringLoading, 
		error: monitoringError, 
		refreshData: refreshMonitoring, 
		fetchActivePhase,
		activePhase,
		isLoadingPhase,
		uploadF100File,
		markAsPaid,
		fetchPhasesList,
		phasesList,
		isLoadingPhasesList,
		updatePhase,
		notifyDataUpload,
		pagination
	} = useAdminMonitoringData();
	const { cropData, isLoading: cropLoading, fetchCropById } = useGetCropById();
	const { 
		invoiceDetails, 
		setInvoiceDetails, 
		isLoading: invoiceLoading, 
		fetchInvoiceDetails, 
		saveInvoiceDetails,
		generatePdf,
		pdfUrl,
		setPdfUrl,
		isGeneratingPdf,
		submitInvoice,
		isSubmittingInvoice
	} = useInvoiceDetails();
	
	// Update page state to use pagination from API (subtract 1 because MUI uses 0-based pagination)
	const [page, setPage] = useState<number>(0);
	const [rowsPerPage, setRowsPerPage] = useState<number>(10);
	const [selectedRecord, setSelectedRecord] = useState<MonitoringAdminData | null>(null);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isMarkingAsPaid, setIsMarkingAsPaid] = useState<Record<string, boolean>>({});
	const [isPhasesModalOpen, setIsPhasesModalOpen] = useState(false);
	const [selectedPhase, setSelectedPhase] = useState<string>('');
	const [isUpdatingPhase, setIsUpdatingPhase] = useState(false);
	const [isDataUploadModalOpen, setIsDataUploadModalOpen] = useState(false);
	const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
	const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
	const [selectedLocation, setSelectedLocation] = useState<{lat: number, lng: number, name: string} | null>(null);
	const [isPaymentConfirmModalOpen, setIsPaymentConfirmModalOpen] = useState(false);
	const [recordToMarkAsPaid, setRecordToMarkAsPaid] = useState<MonitoringAdminData | null>(null);
	const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
	const [editableInvoiceDetails, setEditableInvoiceDetails] = useState<InvoiceDetails | null>(null);

	const [searchTerm, setSearchTerm] = useState('');
	const [selectedUserType, setSelectedUserType] = useState<'farmer' | 'specialist'>('farmer');

	// Add a state variable to track whether a search has been performed
	const [hasSearched, setHasSearched] = useState(false);
	
	// Static cache for crop data and in-flight requests
	const cropCacheRef = useRef<Record<string, string>>({});
	const pendingRequestsRef = useRef<Record<string, Promise<string>>>({});
	
	// Add a state to track if user is actively entering company info
	const [isActivelyEnteringCompanyInfo, setIsActivelyEnteringCompanyInfo] = useState(false);
	
	// Function to get crop name with static caching and request deduplication
	const getCropName = async (cropId: string) => {
		if (!cropId) return 'N/A';
		
		// Check static cache first
		if (cropCacheRef.current[cropId]) {
			return cropCacheRef.current[cropId];
		}
		
		// Check if there's already a pending request for this crop ID
		if (cropId in pendingRequestsRef.current) {
			return pendingRequestsRef.current[cropId]; // Return the existing promise
		}
		
		// Create a new promise for this request and store it
		const promise = (async () => {
			try {
				const cropData = await fetchCropById(cropId);
				const name = cropData && cropData.name ? cropData.name : 'N/A';
				// Update static cache
				cropCacheRef.current[cropId] = name;
				return name;
			} catch (error) {
				console.error('Error fetching crop data:', error);
				return 'Error';
			} finally {
				// Remove from pending requests
				delete pendingRequestsRef.current[cropId];
			}
		})();
		
		// Store the promise
		pendingRequestsRef.current[cropId] = promise;
		
		return promise;
	};

	// Update page and rowsPerPage when pagination changes from API
	useEffect(() => {
		if (pagination) {
			// Convert from 1-based (API) to 0-based (MUI) pagination
			setPage(pagination.page - 1);
			setRowsPerPage(pagination.per_page);
		}
	}, [pagination]);

	// Fetch data only when component mounts
	useEffect(() => {
		// Set page title
		document.title = 'Admin Dashboard - TelAgri';
		
		// Initial data fetch - only on mount
		refreshMonitoring(1, rowsPerPage);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleLogout = async () => {
		await logout();
		window.location.href = '/login';
	};

	const handleSearch = async () => {
		// Call the user search function instead of refreshMonitoring
		if (searchTerm.trim()) {
			setHasSearched(true);
			await searchUsers(searchTerm);
		}
	};

	const handleKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter') {
			// Call the user search function on Enter key
			handleSearch();
			// Prevent default form submission
			event.preventDefault();
		}
	};

	const handleSwitchToRole = async (email: string, role: string) => {
		const userType = role.toLowerCase() as 'farmer' | 'specialist';
		await switchToUser({ email, userType });
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	const getRoleColor = (role: string) => {
		switch (role.toLowerCase()) {
			case 'farmer':
				return 'success';
			case 'specialist':
				return 'primary';
			case 'admin':
				return 'error';
			default:
				return 'default';
		}
	};
	
	// Monitoring data handlers
	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
		// Add 1 to newPage because MUI pagination is 0-based, but API is 1-based
		refreshMonitoring(newPage + 1, rowsPerPage, searchTerm);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newRowsPerPage = parseInt(event.target.value, 10);
		setRowsPerPage(newRowsPerPage);
		setPage(0);
		// Fetch data with new rowsPerPage and reset to page 1
		refreshMonitoring(1, newRowsPerPage, searchTerm);
	};

	const handleEditClick = async (record: MonitoringAdminData) => {
		setSelectedRecord(record);
		setIsEditModalOpen(true);
		
		// Fetch active phases for this record
		if (record.id) {
			await fetchActivePhase(record.id);
		}
	};

	const handleCloseEditModal = () => {
		setIsEditModalOpen(false);
		setSelectedRecord(null);
		setUploadedFiles([]);
	};

	const handleSubmitF100 = async () => {
		if (!selectedRecord || !activePhase.phases_id) return;
		
		setIsUpdating(true);
		try {
			let success = false;
			
			if (uploadedFiles.length > 0) {
				const fileStatus = uploadedFiles[0];
				if (fileStatus && fileStatus.isCompleted && fileStatus.key) {
					// Just pass the key directly, the service will handle it
					success = await uploadF100File(
						activePhase.phases_id,
						fileStatus.key,
						selectedRecord.farmer_id || '',
						activePhase.monitoring_id || selectedRecord.id
					);
				}
			}
			
			if (success) {
				handleCloseEditModal();
			}
		} finally {
			setIsUpdating(false);
		}
	};

	const handleMarkAsPaid = async (record: MonitoringAdminData) => {
		if (!record.id) return;
		
		// Open confirmation modal instead of immediately marking as paid
		setRecordToMarkAsPaid(record);
		setIsPaymentConfirmModalOpen(true);
	};

	const confirmMarkAsPaid = async () => {
		if (!recordToMarkAsPaid || !recordToMarkAsPaid.id) return;
		
		setIsMarkingAsPaid(prev => ({ ...prev, [recordToMarkAsPaid.id]: true }));
		try {
			await markAsPaid(recordToMarkAsPaid.id);
			setIsPaymentConfirmModalOpen(false);
			setRecordToMarkAsPaid(null);
		} finally {
			setIsMarkingAsPaid(prev => ({ ...prev, [recordToMarkAsPaid.id]: false }));
		}
	};

	const cancelMarkAsPaid = () => {
		setIsPaymentConfirmModalOpen(false);
		setRecordToMarkAsPaid(null);
	};

	const handleOpenPhasesModal = async (record: MonitoringAdminData) => {
		if (!record.id) return;
		
		setSelectedRecord(record);
		setSelectedPhase('');
		setIsPhasesModalOpen(true);
		
		// Fetch phases list for this record
		await fetchPhasesList(record.id);
		
		// Also fetch active phase for this record
		await fetchActivePhase(record.id);
	};

	const handleClosePhasesModal = () => {
		setIsPhasesModalOpen(false);
		setSelectedRecord(null);
	};

	const handlePhaseChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedPhase(event.target.value);
	};

	const handleUpdatePhase = async () => {
		if (!selectedRecord || !selectedPhase) return;
		
		setIsUpdatingPhase(true);
		try {
			const success = await updatePhase(selectedPhase, selectedRecord.id);
			if (success) {
				// Refresh monitoring data before closing the modal
				await refreshMonitoring();
				handleClosePhasesModal();
			}
		} finally {
			setIsUpdatingPhase(false);
		}
	};

	// Data upload modal handlers
	const handleOpenDataUploadModal = async (record: MonitoringAdminData) => {
		if (!record.id) return;
		
		setSelectedRecord(record);
		setIsDataUploadModalOpen(true);
		
		// Fetch active phase for this record (same as F100 modal)
		await fetchActivePhase(record.id);
	};

	const handleCloseDataUploadModal = () => {
		setIsDataUploadModalOpen(false);
		setSelectedRecord(null);
	};

	const handleSubmitDataUpload = async () => {
		if (!selectedRecord || !selectedRecord.id) return;
		
		setIsUpdating(true);
		try {
			// Call the admin-data-upload-notification endpoint with the monitoring ID
			const success = await notifyDataUpload(selectedRecord.id);
			if (success) {
				refreshMonitoring();
				handleCloseDataUploadModal();
			}
		} catch (error) {
			console.error('Error sending data upload notification:', error);
		} finally {
			setIsUpdating(false);
		}
	};

	const handleFileUploaded = (status: any) => {
		setUploadedFiles([status]);
	};

	const handleOpenLocationModal = (record: MonitoringAdminData) => {
		if (record.latitude && record.longitude) {
			setSelectedLocation({
				lat: record.latitude,
				lng: record.longitude,
				name: record.location_name || ''
			});
			setIsLocationModalOpen(true);
		}
	};

	const handleCloseLocationModal = () => {
		setIsLocationModalOpen(false);
		setSelectedLocation(null);
	};

	// Invoice details handlers
	const handleOpenInvoiceModal = async (record: MonitoringAdminData) => {
		setSelectedRecord(record);
		setIsInvoiceModalOpen(true); // Open modal immediately to show loading state
		setPdfUrl(null); // Clear any previous PDF URL
		setIsActivelyEnteringCompanyInfo(false); // Reset the active entry state
		
		if (record.id) {
			try {
				const details = await fetchInvoiceDetails(record.id);
				if (details) {
					// Set default price of 1500 if not already set
					setEditableInvoiceDetails({ 
						...details,
						price: details.price !== undefined ? details.price : 1500
					});
				} else {
					// Initialize with empty data if no details found
					setEditableInvoiceDetails({
						id: record.id,
						first_name: '',
						last_name: '',
						id_number: '',
						email: '',
						mobile_number: '',
						company_name: null,
						company_id_number: null,
						representative_first_name: null,
						representative_last_name: null,
						price: 1500 // Default price
					});
				}
			} catch (error) {
				console.error('Error fetching invoice details:', error);
				// Still initialize with empty data if there's an error
				setEditableInvoiceDetails({
					id: record.id,
					first_name: '',
					last_name: '',
					id_number: '',
					email: '',
					mobile_number: '',
					company_name: null,
					company_id_number: null,
					representative_first_name: null,
					representative_last_name: null,
					price: 1500 // Default price
				});
			}
		}
	};

	const handleCloseInvoiceModal = () => {
		setIsInvoiceModalOpen(false);
		setSelectedRecord(null);
		setEditableInvoiceDetails(null);
		setIsActivelyEnteringCompanyInfo(false); // Reset the active entry state
		
		// No longer refreshing data here - we'll only refresh after saving
	};

	const handleInvoiceInputChange = (field: keyof InvoiceDetails, value: string) => {
		if (editableInvoiceDetails) {
			const updatedDetails = {
				...editableInvoiceDetails,
				[field]: field === 'price' ? (value ? Math.max(0, Number(value)) : 0) : value
			};
			
			// If company fields are being filled, clear personal fields and vice versa
			if (field === 'representative_first_name' || field === 'representative_last_name' || field === 'company_id_number' || field === 'company_name') {
				// If any company field is filled, clear personal fields
				if (value) {
					updatedDetails.first_name = '';
					updatedDetails.last_name = '';
					updatedDetails.id_number = '';
					// Set the flag that user is actively entering company info
					setIsActivelyEnteringCompanyInfo(true);
				}
			} else if (field === 'first_name' || field === 'last_name' || field === 'id_number') {
				// If any personal field is filled, clear company fields
				if (value) {
					updatedDetails.representative_first_name = null;
					updatedDetails.representative_last_name = null;
					updatedDetails.company_id_number = null;
					updatedDetails.company_name = null;
					// Reset the flag since user is entering personal info
					setIsActivelyEnteringCompanyInfo(false);
				}
			}
			
			setEditableInvoiceDetails(updatedDetails);
		}
	};

	// Function to check if any company field has data
	const isCompanyInfoEntered = () => {
		if (!editableInvoiceDetails) return false;
		
		return !!(
			editableInvoiceDetails.company_name || 
			editableInvoiceDetails.company_id_number || 
			editableInvoiceDetails.representative_first_name || 
			editableInvoiceDetails.representative_last_name
		);
	};

	const handleSaveInvoiceDetails = async () => {
		if (!selectedRecord?.id || !editableInvoiceDetails) return;
		
		// First save the invoice details
		setIsUpdating(true);
		try {
			const success = await saveInvoiceDetails(selectedRecord.id, editableInvoiceDetails);
			
			// If PDF URL is available, also submit the invoice and send email
			if (success && pdfUrl) {
				await submitInvoice(selectedRecord.id, editableInvoiceDetails.id, pdfUrl, editableInvoiceDetails.price);
				// Close modal immediately
				handleCloseInvoiceModal();
				// Refresh data in background
				refreshMonitoring();
			} else if (success) {
				// Close modal immediately
				handleCloseInvoiceModal();
				// Refresh data in background
				refreshMonitoring();
			}
		} finally {
			setIsUpdating(false);
		}
	};

	const handleGeneratePdf = async () => {
		if (!selectedRecord?.id || !editableInvoiceDetails) return;
		
		try {
			const pdfUrl = await generatePdf(selectedRecord.id, editableInvoiceDetails);
			if (!pdfUrl) {
				// If no URL was returned, show a more specific error message
				console.error('Failed to generate PDF: No URL returned');
			}
		} catch (error) {
			console.error('Error generating PDF:', error);
		}
	};

	const handleOpenPdfLink = () => {
		if (pdfUrl) {
			window.open(pdfUrl, '_blank');
		}
	};

	return (
		<Box
			sx={{
				minHeight: '100vh',
				backgroundColor: '#f5f5f5',
				padding: 3,
			}}
		>
			{/* Header */}
			<Paper
				elevation={3}
				sx={{
					maxWidth: 1200,
					margin: '0 auto 24px auto',
					padding: 3,
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<Box>
					<Typography variant="h4" component="h1" color="primary" sx={{ fontWeight: 'bold' }}>
						Admin Dashboard
					</Typography>
					<Typography variant="subtitle1" color="text.secondary">
						TelAgri Platform Administration
					</Typography>
				</Box>

				<Button
					variant="contained"
					color="error"
					startIcon={<LogoutIcon />}
					onClick={handleLogout}
					sx={{ minWidth: 120 }}
				>
					Logout
				</Button>
			</Paper>

			{/* Search Section */}
			<Paper
				elevation={3}
				sx={{
					maxWidth: 1200,
					margin: '0 auto 24px auto',
					padding: 3,
				}}
			>
				<Typography
					variant="h6"
					gutterBottom
					sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
				>
					<SearchIcon color="primary" />
					User Search
				</Typography>

				<Box sx={{ display: 'flex', mb: 2, gap: 1 }}>
					<TextField
						label={t('Search')}
						variant="outlined"
						size="small"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						onKeyPress={handleKeyPress}
						fullWidth
						InputProps={{
							endAdornment: searchTerm && (
								<InputAdornment position="end">
									<IconButton
										size="small"
										onClick={() => {
											setSearchTerm('');
											setUsers([]);  // Clear search results
											setHasSearched(false); // Reset search state
										}}
									>
										<ClearIcon />
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
					<Button
						variant="contained"
						color="primary"
						onClick={handleSearch}
						startIcon={searchLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
						disabled={searchLoading}
					>
						{searchLoading ? t('Searching...') : t('Search')}
					</Button>
				</Box>

				{searchError && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{searchError}
					</Alert>
				)}

				<Typography variant="body2" color="text.secondary">
					<strong>Search tips:</strong> You can search by partial email (e.g., "john"), domain
					(e.g., "@gmail.com"), or any email pattern.
				</Typography>
			</Paper>

			{/* Loading State */}
			{searchLoading && (
				<Paper
					elevation={3}
					sx={{
						maxWidth: 1200,
						margin: '0 auto',
						padding: 4,
						textAlign: 'center',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<CircularProgress size={40} sx={{ mb: 2 }} />
					<Typography variant="h6" color="text.secondary" gutterBottom>
						{t('Searching for users...')}
					</Typography>
				</Paper>
			)}

			{/* Results Section */}
			{!searchLoading && users.length > 0 && (
				<Paper
					elevation={3}
					sx={{
						maxWidth: 1200,
						margin: '0 auto',
						padding: 3,
					}}
				>
					<Typography
						variant="h6"
						gutterBottom
						sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
					>
						<PersonIcon color="primary" />
						Search Results ({users.length} users found)
					</Typography>

					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>
										<strong>Email</strong>
									</TableCell>
									<TableCell>
										<strong>Name</strong>
									</TableCell>
									<TableCell>
										<strong>Role</strong>
									</TableCell>
									<TableCell>
										<strong>Status</strong>
									</TableCell>
									<TableCell>
										<strong>Created</strong>
									</TableCell>
									<TableCell>
										<strong>Actions</strong>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{users.map((user, index) => (
									<TableRow key={index} hover>
										<TableCell>
											<Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
												{user.email}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												N/A
											</Typography>
										</TableCell>
										<TableCell>
											<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
												{user.roles.map((roleObj, roleIndex) => (
													<Chip
														key={roleIndex}
														label={roleObj.role.toLowerCase()}
														color={getRoleColor(roleObj.role) as any}
														size="small"
														variant={roleObj.isVerified ? 'filled' : 'outlined'}
													/>
												))}
											</Box>
										</TableCell>
										<TableCell>
											<Chip
												label={user.status === 'ACTIVE' ? 'Active' : 'Inactive'}
												color={user.status === 'ACTIVE' ? 'success' : 'default'}
												size="small"
											/>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{formatDate(user.createdAt)}
											</Typography>
										</TableCell>
										<TableCell>
											<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
												{user.roles
													.filter(
														(roleObj) => roleObj.role === 'FARMER' || roleObj.role === 'SPECIALIST'
													)
													.map((roleObj, roleIndex) => (
														<Tooltip
															key={roleIndex}
															title={`Switch to this ${roleObj.role.toLowerCase()}`}
														>
															<IconButton
																color="primary"
																onClick={() => handleSwitchToRole(user.email, roleObj.role)}
																disabled={switchLoading}
																size="small"
															>
																<SwapHorizIcon />
															</IconButton>
														</Tooltip>
													))}
											</Box>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
			)}

			{/* Empty State - Only show when a search has been performed but no results were found */}
			{!searchLoading && users.length === 0 && searchTerm && hasSearched && (
				<Paper
					elevation={3}
					sx={{
						maxWidth: 1200,
						margin: '0 auto',
						padding: 4,
						textAlign: 'center',
					}}
				>
					<Typography variant="h6" color="text.secondary" gutterBottom>
						No users found
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Try adjusting your search term or search for a different email pattern.
					</Typography>
				</Paper>
			)}

			{/* Instructions - Show when no search has been performed or when search term is empty */}
			{!searchLoading && (users.length === 0 && (!searchTerm || !hasSearched)) && (
				<Paper
					elevation={3}
					sx={{
						maxWidth: 1200,
						margin: '0 auto',
						padding: 4,
						textAlign: 'center',
						marginBottom: 3,
					}}
				>
					<Typography variant="h6" gutterBottom>
						Welcome to the Admin Panel
					</Typography>
					<Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
						Use the search functionality above to find and manage users.
					</Typography>

					<Box sx={{ mt: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
						<Typography variant="body2" color="text.secondary">
							<strong>Available Actions:</strong>
							<br />
							• Search for users by email pattern
							<br />
							• View user details and status
							<br />• Switch to farmer or specialist accounts for testing
						</Typography>
					</Box>
				</Paper>
			)}
      
      {/* Monitoring Data Section */}
      <Paper
        elevation={3}
        sx={{
          maxWidth: 1200,
          margin: '24px auto',
          padding: 3,
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <BarChartIcon color="primary" />
          Monitoring Data
        </Typography>
        
        {monitoringLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : monitoringError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {monitoringError}
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Farmer</TableCell>
                    <TableCell>Area & Unit</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Crop</TableCell>
                    <TableCell>Farmer Email</TableCell>
                    <TableCell>Specialist</TableCell>
                    <TableCell>Monitoring Type</TableCell>
                    <TableCell>Payment Status</TableCell>
                    <TableCell>F100</TableCell>
                    <TableCell>Mark as Paid</TableCell>
                    <TableCell>Phases</TableCell>
                    <TableCell>Data Uploaded</TableCell>
                    <TableCell>Invoice Details</TableCell>
                    <TableCell>View Invoice</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {monitoringData.map((record) => (
                    <TableRow 
                      key={record.id} 
                      hover 
                    >
                      <TableCell>{record.id}</TableCell>
                      <TableCell>{record.farmer}</TableCell>
                      <TableCell>
                        {record.area ? `${record.area} ${record.unit || ''}` : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {record.location_name ? record.location_name.substring(0, 20) + (record.location_name.length > 20 ? '...' : '') : 'N/A'}
                          </Typography>
                          {record.latitude && record.longitude && (
                            <Tooltip title="View on map">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => handleOpenLocationModal(record)}
                              >
                                <PublicOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {record.crop_id ? (
                          <CropNameCell cropId={record.crop_id} getCropName={getCropName} />
                        ) : 'N/A'}
                      </TableCell>
                      <TableCell>{record.farmer_email}</TableCell>
                      <TableCell>{record.specialist}</TableCell>
                      <TableCell>{record.monitoring_type}</TableCell>
                      <TableCell>
                        <Chip
                          label={record.payment_status || 'Unknown'}
                          color={
                            record.payment_status === 'PAID' || 
                            record.payment_status === 'stripe_payed' || 
                            record.payment_status === 'invoice_payed' ? 'success' :
                            record.payment_status === 'PENDING' ? 'warning' :
                            record.payment_status === 'FAILED' ? 'error' : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit F100 File">
                          <IconButton 
                            color="primary" 
                            size="small"
                            onClick={() => handleEditClick(record)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Mark as Paid">
                          <span>
                            <Radio
                              checked={
                                record.payment_status === 'PAID' || 
                                record.payment_status === 'stripe_payed' || 
                                record.payment_status === 'invoice_payed'
                              }
                              onChange={() => handleMarkAsPaid(record)}
                              disabled={
                                isMarkingAsPaid[record.id] || 
                                record.payment_status === 'PAID' || 
                                record.payment_status === 'stripe_payed' || 
                                record.payment_status === 'invoice_payed'
                              }
                              color="success"
                            />
                            {isMarkingAsPaid[record.id] && (
                              <CircularProgress size={16} sx={{ ml: 1 }} />
                            )}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Phases">
                          <IconButton 
                            color="primary" 
                            size="small"
                            onClick={() => handleOpenPhasesModal(record)}
                          >
                            <PaymentIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Data Uploaded">
                          <IconButton 
                            color="primary" 
                            size="small"
                            onClick={() => handleOpenDataUploadModal(record)}
                          >
                            <CloudUploadIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      												<TableCell>
													<Tooltip title="Invoice Details">
														<IconButton 
															color="primary" 
															size="small"
															onClick={() => handleOpenInvoiceModal(record)}
														>
															<ReceiptIcon />
														</IconButton>
													</Tooltip>
												</TableCell>
												<TableCell>
													{record.invoice_url && (
														<Tooltip title={t('viewInvoice')}>
															<IconButton 
																color="success" 
																size="small"
																onClick={() => window.open(record.invoice_url as string, '_blank')}
															>
																<OpenInNewIcon />
															</IconButton>
														</Tooltip>
													)}
												</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={pagination.total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </Paper>

			{/* Loading Overlay */}
			{switchLoading && (
				<Box
					sx={{
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: 'rgba(0, 0, 0, 0.5)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						zIndex: 9999,
					}}
				>
					<Paper sx={{ p: 3, textAlign: 'center' }}>
						<CircularProgress sx={{ mb: 2 }} />
						<Typography>Switching user...</Typography>
					</Paper>
				</Box>
			)}
      
      {/* F100 File Upload Modal */}
      <Dialog 
        open={isEditModalOpen} 
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload F100 File</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              <Box component="span" fontWeight="bold">Farmer:</Box> {selectedRecord?.farmer}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <Box component="span" fontWeight="bold">Specialist:</Box> {selectedRecord?.specialist}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <Box component="span" fontWeight="bold">Monitoring Type:</Box> {selectedRecord?.monitoring_type}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <Box component="span" fontWeight="bold">Payment Status:</Box>{' '}
              {selectedRecord?.payment_status ? (
                <Chip
                  label={selectedRecord.payment_status}
                  color={
                    selectedRecord.payment_status === 'PAID' || 
                    selectedRecord.payment_status === 'stripe_payed' || 
                    selectedRecord.payment_status === 'invoice_payed' ? 'success' :
                    selectedRecord.payment_status === 'PENDING' ? 'warning' :
                    selectedRecord.payment_status === 'FAILED' ? 'error' : 'default'
                  }
                  size="small"
                />
              ) : 'Unknown'}
            </Typography>
            
            {/* Attached Links Section */}
            {activePhase && activePhase.attachedLink && activePhase.attachedLink.length > 0 && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body1" fontWeight="bold" gutterBottom>
                  Attached Reports:
                </Typography>
                <List dense>
                  {activePhase.attachedLink.map((link) => (
                    <ListItem 
                      key={link.id}
                      secondaryAction={
                        <IconButton 
                          edge="end" 
                          aria-label="view" 
                          onClick={() => window.open(link.report_link, '_blank')}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      }
                    >
                      <ListItemIcon>
                        <InsertDriveFileIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary={link.phase_name || 'Report'} 
                        secondary={new Date(link.created_at).toLocaleDateString()} 
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            
            {isLoadingPhase ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <FormControl fullWidth margin="normal">
                <InputLabel>Active Phase</InputLabel>
                <Select
                  value={activePhase.phases_id || ''}
                  label="Active Phase"
                  disabled
                >
                  <MenuItem value={activePhase.phases_id || ''}>
                    {activePhase.name || 'No active phase'}
                  </MenuItem>
                </Select>
              </FormControl>
            )}
            
            <Box sx={{ mt: 2 }}>
              <NewFileUploader
                title="F100 File Upload"
                description="Upload F100 file"
                multiple={false}
                onUploaded={handleFileUploaded}
                allowedFormats={['.pdf', '.doc', '.docx', '.xls', '.xlsx']}
                showSupportedFormats={true}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal} disabled={isUpdating}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitF100} 
            variant="contained" 
            color="primary"
            disabled={isUpdating || !activePhase.phases_id || !uploadedFiles.length || !uploadedFiles[0]?.isCompleted}
          >
            {isUpdating ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Phases Modal */}
      <Dialog 
        open={isPhasesModalOpen} 
        onClose={handleClosePhasesModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Phases</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              <Box component="span" fontWeight="bold">Farmer:</Box> {selectedRecord?.farmer}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <Box component="span" fontWeight="bold">Specialist:</Box> {selectedRecord?.specialist}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <Box component="span" fontWeight="bold">Monitoring Type:</Box> {selectedRecord?.monitoring_type}
            </Typography>
            
            {/* Show active phase */}
            {isLoadingPhase ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
                              <FormControl fullWidth margin="normal">
                <InputLabel>Current Active Phase</InputLabel>
                <Select
                  value={activePhase.phases_id || ''}
                  label="Current Active Phase"
                  disabled
                >
                  <MenuItem value={activePhase.phases_id || ''}>
                    {activePhase.name || 'No active phase'}
                  </MenuItem>
                </Select>
                {activePhase.phases_id && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Current active phase is excluded from the selection below
                  </Typography>
                )}
              </FormControl>
            )}
            
            {/* Select new phase */}
            {isLoadingPhasesList ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <FormControl fullWidth margin="normal">
                <InputLabel>Select New Phase</InputLabel>
                <Select
                  value={selectedPhase}
                  onChange={handlePhaseChange as any}
                  label="Select New Phase"
                >
                  {Array.isArray(phasesList) && phasesList
                    .filter(phase => phase.id !== activePhase.phases_id) // Filter out current active phase
                    .map((phase) => (
                      <MenuItem key={phase.id} value={phase.id}>
                        {phase.phases_name}
                      </MenuItem>
                    ))}
                </Select>
                {Array.isArray(phasesList) && phasesList.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    {phasesList.filter(phase => phase.id !== activePhase.phases_id).length} phases available to select
                  </Typography>
                )}
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePhasesModal}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdatePhase} 
            variant="contained" 
            color="primary"
            disabled={!selectedPhase || isUpdatingPhase}
          >
            {isUpdatingPhase ? <CircularProgress size={24} /> : 'Update Phase'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Data Upload Modal */}
      <Dialog 
        open={isDataUploadModalOpen} 
        onClose={handleCloseDataUploadModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Data Upload Notification</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              <Box component="span" fontWeight="bold">Farmer:</Box> {selectedRecord?.farmer}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <Box component="span" fontWeight="bold">Specialist:</Box> {selectedRecord?.specialist}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <Box component="span" fontWeight="bold">Monitoring Type:</Box> {selectedRecord?.monitoring_type}
            </Typography>
            
            {isLoadingPhase ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <FormControl fullWidth margin="normal">
                <InputLabel>Active Phase</InputLabel>
                <Select
                  value={activePhase.phases_id || ''}
                  label="Active Phase"
                  disabled
                >
                  <MenuItem value={activePhase.phases_id || ''}>
                    {activePhase.name || 'No active phase'}
                  </MenuItem>
                </Select>
                {activePhase.phases_id && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    This will send a notification that data has been uploaded for phase: {activePhase.name}
                  </Typography>
                )}
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDataUploadModal} disabled={isUpdating}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitDataUpload} 
            variant="contained" 
            color="primary"
            disabled={isUpdating || !activePhase.phases_id}
          >
            {isUpdating ? <CircularProgress size={24} /> : 'Submit Notification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location Modal */}
      <GeneralMuiModal
        maxWidth={'xl'}
        title="Location"
        styles={{
          paperStyle: { width: modalDimensions.width, height: modalDimensions.height, padding: 0 },
          contentStyle: { padding: 0 },
        }}
        open={isLocationModalOpen}
        onClose={handleCloseLocationModal}
        content={
          selectedLocation && (
            <TaMaps
              disabled={true}
              marker={{
                lat: selectedLocation.lat,
                lng: selectedLocation.lng
              }}
              currentAddress={selectedLocation.name}
            />
          )
        }
      />

      {/* Payment Confirmation Modal */}
      <Dialog
        open={isPaymentConfirmModalOpen}
        onClose={cancelMarkAsPaid}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Payment Status Change</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to mark this monitoring request as paid?
            </Typography>
            {recordToMarkAsPaid && (
              <>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  <Box component="span" fontWeight="bold">Farmer:</Box> {recordToMarkAsPaid.farmer}
                </Typography>
                <Typography variant="body2">
                  <Box component="span" fontWeight="bold">Specialist:</Box> {recordToMarkAsPaid.specialist}
                </Typography>
                <Typography variant="body2">
                  <Box component="span" fontWeight="bold">Monitoring Type:</Box> {recordToMarkAsPaid.monitoring_type}
                </Typography>
                <Typography variant="body2">
                  <Box component="span" fontWeight="bold">Current Payment Status:</Box>{' '}
                  {recordToMarkAsPaid.payment_status ? (
                    <Chip
                      size="small"
                      label={recordToMarkAsPaid.payment_status}
                      color={
                        recordToMarkAsPaid.payment_status === 'PAID' || 
                        recordToMarkAsPaid.payment_status === 'stripe_payed' || 
                        recordToMarkAsPaid.payment_status === 'invoice_payed' ? 'success' :
                        recordToMarkAsPaid.payment_status === 'PENDING' ? 'warning' :
                        recordToMarkAsPaid.payment_status === 'FAILED' ? 'error' : 'default'
                      }
                    />
                  ) : 'Unknown'}
                </Typography>
              </>
            )}
            <Typography variant="body1" sx={{ mt: 2, color: 'error.main' }}>
              This action cannot be undone.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelMarkAsPaid}>
            Cancel
          </Button>
          <Button 
            onClick={confirmMarkAsPaid} 
            variant="contained" 
            color="primary"
            disabled={isMarkingAsPaid[recordToMarkAsPaid?.id || '']}
          >
            {isMarkingAsPaid[recordToMarkAsPaid?.id || ''] ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Details Modal */}
      <Dialog 
        open={isInvoiceModalOpen} 
        onClose={handleCloseInvoiceModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Invoice Details</DialogTitle>
        <DialogContent>
          {invoiceLoading || !editableInvoiceDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ p: 1 }}>
              {/* Add back the farmer and monitoring type info */}
              <Typography variant="body1" gutterBottom>
                <Box component="span" fontWeight="bold">Farmer:</Box> {selectedRecord?.farmer}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <Box component="span" fontWeight="bold">Monitoring Type:</Box> {selectedRecord?.monitoring_type}
              </Typography>
              
              {/* Only show the notification when user is actively entering company info */}
              {isActivelyEnteringCompanyInfo && (
                <Box 
                  sx={{ 
                    backgroundColor: '#f5f5f5', 
                    p: 2, 
                    mb: 3, 
                    mt: 2,
                    borderRadius: 1,
                    border: '1px solid #e0e0e0'
                  }}
                >
                  <Typography variant="body1" color="primary">
                    თქვენ შეგყავთ კომპანიის ინფორმაცია, ელ ფოსტა, მობილურის ნომერი და ფასი საერთო პარამეტრია.გთხოვს არ გამოგრჩეთ კომპანიის წარმომადგენლის სახელისა და გვარის შეყვანა
                  </Typography>
                </Box>
              )}
              
              {/* PDF Link Section */}
              {pdfUrl && (
                <Box sx={{ mt: 2, mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PictureAsPdfIcon color="error" />
                  <Link
                    component="button"
                    variant="body1"
                    onClick={() => window.open(pdfUrl, '_blank')}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'underline', color: 'primary.main', fontWeight: 'bold' }}
                  >
                    Click to view invoice <OpenInNewIcon fontSize="small" />
                  </Link>
                </Box>
              )}
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Personal Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                  <TextField
                    label="First Name"
                    value={editableInvoiceDetails?.first_name || ''}
                    onChange={(e) => handleInvoiceInputChange('first_name', e.target.value)}
                    sx={{ width: 'calc(50% - 8px)' }}
                  />
                  <TextField
                    label="Last Name"
                    value={editableInvoiceDetails?.last_name || ''}
                    onChange={(e) => handleInvoiceInputChange('last_name', e.target.value)}
                    sx={{ width: 'calc(50% - 8px)' }}
                  />
                  <TextField
                    label="ID Number"
                    value={editableInvoiceDetails?.id_number || ''}
                    onChange={(e) => handleInvoiceInputChange('id_number', e.target.value)}
                    sx={{ width: 'calc(50% - 8px)' }}
                  />
                  <TextField
                    label="Email"
                    value={editableInvoiceDetails?.email || ''}
                    onChange={(e) => handleInvoiceInputChange('email', e.target.value)}
                    sx={{ width: 'calc(50% - 8px)' }}
                  />
                  <TextField
                    label="Mobile Number"
                    value={editableInvoiceDetails?.mobile_number || ''}
                    onChange={(e) => handleInvoiceInputChange('mobile_number', e.target.value)}
                    sx={{ width: 'calc(50% - 8px)' }}
                  />
                  <TextField
                    label="Price"
                    type="number"
                    value={editableInvoiceDetails?.price || ''}
                    onChange={(e) => handleInvoiceInputChange('price', e.target.value)}
                    sx={{ width: 'calc(50% - 8px)' }}
                    required
                    inputProps={{ min: 0 }}
                    InputProps={{
                      startAdornment: <span style={{ marginRight: '8px' }}>€</span>,
                    }}
                    helperText="Required. Default is 1500 EUR"
                  />
                </Box>
                
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                  Company Information (Optional)
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                  <TextField
                    label="Company Name"
                    value={editableInvoiceDetails?.company_name || ''}
                    onChange={(e) => handleInvoiceInputChange('company_name', e.target.value)}
                    sx={{ width: 'calc(50% - 8px)' }}
                  />
                  <TextField
                    label="Company ID Number"
                    value={editableInvoiceDetails?.company_id_number || ''}
                    onChange={(e) => handleInvoiceInputChange('company_id_number', e.target.value)}
                    sx={{ width: 'calc(50% - 8px)' }}
                  />
                  <TextField
                    label="Representative First Name"
                    value={editableInvoiceDetails?.representative_first_name || ''}
                    onChange={(e) => handleInvoiceInputChange('representative_first_name', e.target.value)}
                    sx={{ width: 'calc(50% - 8px)' }}
                  />
                  <TextField
                    label="Representative Last Name"
                    value={editableInvoiceDetails?.representative_last_name || ''}
                    onChange={(e) => handleInvoiceInputChange('representative_last_name', e.target.value)}
                    sx={{ width: 'calc(50% - 8px)' }}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInvoiceModal} disabled={isUpdating || invoiceLoading || !editableInvoiceDetails || isSubmittingInvoice}>
            Cancel
          </Button>
          <Button 
            onClick={handleGeneratePdf} 
            variant="contained" 
            color="secondary"
            disabled={isUpdating || invoiceLoading || !editableInvoiceDetails || isGeneratingPdf || isSubmittingInvoice || !editableInvoiceDetails?.price}
            startIcon={isGeneratingPdf ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
            sx={{ mr: 1 }}
          >
            {isGeneratingPdf ? 'Generating...' : 'Generate Invoice'}
          </Button>
          <Button 
            onClick={handleSaveInvoiceDetails} 
            variant="contained" 
            color="primary"
            disabled={isUpdating || invoiceLoading || !editableInvoiceDetails || isSubmittingInvoice || !editableInvoiceDetails?.price}
            startIcon={isSubmittingInvoice ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {isSubmittingInvoice ? 'Sending...' : pdfUrl ? 'Save & Send Email' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
		</Box>
	);
};
